import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { WorkOrder } from '../components/model/workorder';
import { WorkOrderLineView } from '../components/work-order-line-list/model/work-order-line-view.model';

export interface WorkOrderPdfContext {
  plantName: string;
  siteName: string;
  dealerName: string;
  createdBy: string;
  customerAddress: string;
  customerCity: string;
  customerState: string;
  customerZip: string;
  contact: string;
  zoneImage?: string; // base64 encoded image string
  associateDealerCustomer?: string; // displayed conditionally when an associate dealer relationship exists
}

@Injectable({
  providedIn: 'root'
})
export class WorkorderPdfExportService {
  /** Factory method for jsPDF instance — overridable in tests */
  protected createDoc(): jsPDF {
    return new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  }

  /** Wrapper for autoTable — overridable in tests */
  protected applyAutoTable(doc: jsPDF, options: any): void {
    autoTable(doc, options);
  }

  /**
   * Generate a PDF for the entire Work Order including header and all lines.
   */
  generateWorkOrderPdf(workOrder: WorkOrder, lines: WorkOrderLineView[], context: WorkOrderPdfContext): void {
    // Page setup: A4 landscape with margins matching iText Document(PageSize.A4.rotate(), 24, 24, 20+100, 36)
    // 24pt ≈ 8.5mm (left/right), 120pt ≈ 42.3mm (top: 20pt margin + 100pt header), 36pt ≈ 12.7mm (bottom)
    const marginLeft = 8.5;
    const marginRight = 8.5;
    const marginTop = 7; // ~20pt — top page margin where header starts
    const headerHeight = 46; // reserved header area (accommodates centered customer block)
    const marginBottom = 12.7; // ~36pt

    const doc = this.createDoc();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const lineHeight = 6;
    const y = marginTop;

    // === HEADER: left (logo + plant/site/contact) | center (customer) | right (work order) ===
    const centerX = pageWidth / 2;
    const rightX = pageWidth - marginRight;

    // --- Left column: Dealer logo ---
    if (context.zoneImage) {
      try {
        let imageData = context.zoneImage;
        let format = 'PNG';
        if (imageData.startsWith('data:image/')) {
          const match = imageData.match(/^data:image\/(png|jpeg|jpg|gif);base64,/i);
          if (match) {
            format = match[1].toUpperCase() === 'JPG' ? 'JPEG' : match[1].toUpperCase();
          }
        } else {
          imageData = `data:image/png;base64,${imageData}`;
        }
        // Left-align logo within the left column
        const imgW = 50;
        const imgH = 22;
        const imgX = marginLeft;
        const imgY = y;
        doc.setFillColor(0, 0, 0);
        doc.rect(imgX, imgY, imgW, imgH, 'F');
        doc.addImage(imageData, format, imgX, imgY, imgW, imgH);
      } catch (e) {
        console.warn('Failed to render zone image in PDF:', e);
      }
    }

    // --- Left column (below logo): Plant, Site, Contact ---
    const leftColY = y + 26;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`Plant: ${context.plantName || ''}`, marginLeft, leftColY);
    doc.text(`Site: ${context.siteName || ''}`, marginLeft, leftColY + lineHeight);
    doc.text(`Contact: ${context.contact || ''}`, marginLeft, leftColY + lineHeight * 2);

    // --- Center column (centered): Title, Customer details, Associate Dealer ---
    let cy = y + 3;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Retread Work Order', centerX, cy, { align: 'center' });
    cy += 8;
    doc.setFontSize(10);
    doc.text(`${workOrder.customerName || ''}`, centerX, cy, { align: 'center' });
    cy += 5;
    doc.setFontSize(9);
    doc.text(`${context.customerAddress || ''}`, centerX, cy, { align: 'center' });
    cy += 5;
    const cityStateZip = [context.customerCity, context.customerState, context.customerZip].filter(Boolean).join(' ');
    if (cityStateZip) {
      doc.text(cityStateZip, centerX, cy, { align: 'center' });
      cy += 5;
    }
    doc.text(`Customer Id: ${workOrder.customerCode || ''}`, centerX, cy, { align: 'center' });
    cy += 5;
    if (workOrder.customerNote) {
      doc.text(`Customer Note: ${workOrder.customerNote}`, centerX, cy, { align: 'center' });
      cy += 5;
    }
    doc.text(`Customer Ref#: ${workOrder.crn || ''}`, centerX, cy, { align: 'center' });
    cy += 6;

    // Conditionally display Associate Dealer Customer (e.g., Belisle) when applicable
    if (context.associateDealerCustomer) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`(${context.associateDealerCustomer})`, centerX, cy, { align: 'center' });
    }

    // --- Right column (right-aligned): Work Order#, Casing Total, Created By, Created Date ---
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`Work Order#: ${workOrder.workOrderNumber || ''}`, rightX, y, { align: 'right' });
    doc.text(`Total Tag(s): ${workOrder.numberOfLines ?? lines.length}`, rightX, y + lineHeight, { align: 'right' });
    doc.text(`Created By: ${context.createdBy || ''}`, rightX, y + lineHeight * 3, { align: 'right' });
    doc.text(`Created Date: ${workOrder.receivedDate || ''}`, rightX, y + lineHeight * 4, { align: 'right' });

    // Separator line at end of header area
    const separatorY = marginTop + headerHeight;
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.line(marginLeft, separatorY, pageWidth - marginRight, separatorY);

    // === LINES TABLE ===
    const columns = [
      { header: 'Tire', dataKey: 'lineNumber' },
      { header: 'Tag', dataKey: 'casingId' },
      { header: 'PC', dataKey: 'processCode' },
      { header: 'WT', dataKey: 'workType' },
      { header: 'Location', dataKey: 'shippingLocation' },
      { header: 'Status', dataKey: 'casingStatus' },
      { header: 'Make', dataKey: 'make' },
      { header: 'Series', dataKey: 'series' },
      { header: 'Size', dataKey: 'size' },
      { header: 'Tread', dataKey: 'tread' },
      { header: 'DOT', dataKey: 'mfgDot' },
      { header: 'Brand', dataKey: 'brand' },
      { header: 'NTR', dataKey: 'ntr' },
      { header: 'RTD', dataKey: 'rtDot' },
      { header: 'Wheel Type', dataKey: 'wheelType' },
      { header: 'Wheel Colour', dataKey: 'wheelColor' },
      { header: 'Repair', dataKey: 'repairs' },
      { header: 'Scrap Rej', dataKey: 'scrapCodeDescription' },
      { header: 'Rej. Code', dataKey: 'rejectReason' },
      { header: 'Disposition', dataKey: 'disposition' }
    ];

    const tableData = lines.map((line) => columns.map((c) => (line as any)[c.dataKey] || ''));

    this.applyAutoTable(doc, {
      startY: separatorY + 3,
      head: [
        [{ content: 'Main Casing Table', colSpan: columns.length, styles: { halign: 'center', fontSize: 8, fontStyle: 'bold' } }],
        columns.map((c) => c.header)
      ],
      body: tableData,
      theme: 'grid',
      styles: {
        fontSize: 6,
        cellPadding: 1,
        fontStyle: 'bold',
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
        overflow: 'linebreak'
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 6,
        lineColor: [0, 0, 0],
        lineWidth: 0.3
      },
      margin: { left: marginLeft, right: marginRight, bottom: marginBottom + 5 }
    });

    // Footer with page numbers
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text(`Generated: ${new Date().toLocaleString()}`, marginLeft, pageHeight - marginBottom + 3);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - marginRight - 20, pageHeight - marginBottom + 3);
    }

    doc.save(`WO_${workOrder.workOrderNumber || 'export'}.pdf`);
  }
}
